import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, Image as ImgIcon, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Visual image picker that opens a dialog with the user's library.
 * Props:
 *  - value (string): currently selected URL/dataURL
 *  - onChange (fn): called with the picked URL
 *  - label (string)
 *  - testid (string)
 */
export default function ImagePicker({ value, onChange, label = "Imagen", testid = "image-picker" }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (open) api.get("/library/photos").then((r) => setPhotos(r.data)).catch(() => {});
  }, [open]);

  const filtered = filter
    ? photos.filter((p) => (p.label || "").toLowerCase().includes(filter.toLowerCase()))
    : photos;

  const select = (url) => {
    onChange(url);
    setOpen(false);
  };

  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-[#5C616A] mb-1">{label}</p>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="relative">
            <img src={value} alt="seleccionada" className="w-20 h-14 object-cover rounded-sm border border-[#E2DCD0]" />
            <button
              onClick={() => onChange("")}
              type="button"
              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-[#E2DCD0] flex items-center justify-center"
              aria-label="Quitar"
            >
              <X className="w-3 h-3 text-[#9D0208]" />
            </button>
          </div>
        ) : (
          <div className="w-20 h-14 rounded-sm border border-dashed border-[#E2DCD0] flex items-center justify-center bg-[#F9F8F6]">
            <ImgIcon className="w-4 h-4 text-[#8A8F98]" />
          </div>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={testid} type="button" variant="outline" size="sm" className="rounded-sm border-[#E2DCD0]">
              {value ? "Cambiar" : "Elegir de biblioteca"}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white rounded-sm max-w-4xl">
            <DialogHeader><DialogTitle className="font-display">Biblioteca de imágenes</DialogTitle></DialogHeader>
            <Input
              placeholder="Buscar por etiqueta..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="rounded-sm border-[#E2DCD0]"
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-[480px] overflow-y-auto py-2">
              {filtered.length === 0 && (
                <p className="col-span-full text-center text-[#8A8F98] text-sm py-12">
                  No hay fotos en tu biblioteca. Súbelas desde el módulo Biblioteca.
                </p>
              )}
              {filtered.map((p) => {
                const isSelected = value === p.url;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => select(p.url)}
                    className={`relative rounded-sm overflow-hidden border ${isSelected ? "border-[#D95D39] ring-2 ring-[#D95D39]" : "border-[#E2DCD0]"} hover:border-[#D95D39] transition-colors`}
                    data-testid={`picker-photo-${p.id}`}
                  >
                    <img src={p.url} alt={p.label} className="w-full h-32 object-cover" />
                    <div className="p-1.5 text-[10px] text-[#5C616A] truncate bg-white text-left">{p.label}</div>
                    {isSelected && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#D95D39] flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
